import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import TipTapEditor from '@/components/TipTapEditor';
import MediaPickerModal from '@/components/MediaPickerModal';
import CommercialLabelSelect from '@/components/CommercialLabelSelect';
import { OrgMatchDialog, PersonMatchDialog, LocationMatchDialog } from '@/components/EntityMatchDialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import {
  ArrowRight, ArrowLeft, Send, Image as ImageIcon, X, Plus,
  AlertCircle, CheckCircle, Settings, Trash2, RefreshCw,
  Sparkles, Loader2, Search, Check, MapPin,
} from 'lucide-react';
import { toast } from 'sonner';

const defaultStoryTypes = [
  { value: 'press_release',      label: 'Press Release' },
  { value: 'executive_move',     label: 'Executive Move' },
  { value: 'funding',            label: 'Funding' },
  { value: 'partnership',        label: 'Partnership' },
  { value: 'product_launch',     label: 'Product Launch' },
  { value: 'regulation',         label: 'Regulation' },
  { value: 'tender_procurement', label: 'Tender & Procurement' },
  { value: 'legal',              label: 'Legal' },
  { value: 'event',              label: 'Event' },
  { value: 'market_update',      label: 'Market Update' },
  { value: 'other',              label: 'Other' },
];

function calcScore(f) {
  let s = 0;
  if (f.headline) s += 15;
  if (f.lede) s += 15;
  if (f.body && f.body !== '<p></p>') s += 15;
  if (f.source_link) s += 10;
  if (f.image) s += 10;
  if (f.client_name) s += 10;
  if (f.project_name) s += 5;
  if (f.publication_ids?.length > 0) s += 10;
  if (f.tags?.length > 0) s += 10;
  return s;
}

async function fetchWithRetry(fn, retries = 3, delayMs = 1500) {
  for (let i = 0; i < retries; i++) {
    try { return await fn(); } catch {
      if (i < retries - 1) await new Promise(res => setTimeout(res, delayMs));
    }
  }
  return null;
}

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ─── Regex-based tag/area matching (same logic as backend auto-tagging) ───

function matchTermsInText(terms, text) {
  const lower = text.toLowerCase();
  return terms.filter(term => {
    const pattern = new RegExp(`\\b${term.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`);
    return pattern.test(lower);
  });
}

// ─── Entity Search Combobox ───

function EntitySearchCombobox({ entityType, value, onSelect, onClear, placeholder }) {
  const [query, setQuery]     = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen]       = useState(false);
  const debouncedQuery        = useDebounce(query, 150);
  const wrapperRef            = useRef(null);
  const cache                 = useRef({});

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) { setResults([]); setLoading(false); return; }
    const cacheKey = `${entityType}:${debouncedQuery.toLowerCase()}`;
    if (cache.current[cacheKey]) { setResults(cache.current[cacheKey]); return; }
    setLoading(true);
    const endpoint = entityType === 'organisation' ? '/organisations/match' : '/persons/match';
    api.get(endpoint, { params: { q: debouncedQuery } })
      .then(res => { const d = res.data || []; cache.current[cacheKey] = d; setResults(d); })
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [debouncedQuery, entityType]);

  if (value) {
    return (
      <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
        <div>
          <p className="text-sm font-medium text-foreground">{value.name}</p>
          {value.title && <p className="text-xs text-[#9C8E84]">{value.title}</p>}
          {value.aliases?.length > 0 && (
            <p className="text-xs text-[#9C8E84]">aka {value.aliases.slice(0, 2).join(', ')}</p>
          )}
        </div>
        <Button variant="ghost" size="icon" className="h-6 w-6 text-[#9C8E84] hover:text-red-500 hover:bg-red-50" onClick={onClear}>
          <X size={13} />
        </Button>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder || `Search ${entityType}s...`}
          className="pl-8 pr-8 bg-white text-[#1F1A17] border-border"
        />
        {loading
          ? <Loader2 size={13} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />
          : query && (
            <button onClick={() => { setQuery(''); setResults([]); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <X size={13} />
            </button>
          )
        }
      </div>
      {open && query.length >= 2 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-border rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {loading && (
            <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 size={13} className="animate-spin" /> Searching...
            </div>
          )}
          {!loading && results.length === 0 && (
            <div className="px-3 py-2 text-sm text-muted-foreground">No results — use + to create new</div>
          )}
          {!loading && results.map(r => (
            <button
              key={r.id}
              onClick={() => { onSelect(r); setQuery(''); setOpen(false); setResults([]); }}
              className="w-full text-left px-3 py-2 hover:bg-[#FFF4E8] text-sm flex items-center justify-between gap-2"
            >
              <div className="min-w-0">
                <span className="text-foreground font-medium">{r.name}</span>
                {r.title && <span className="text-xs text-muted-foreground ml-2">{r.title}</span>}
                {r.aliases?.length > 0 && (
                  <span className="text-xs text-muted-foreground ml-2">aka {r.aliases.slice(0, 1).join(', ')}</span>
                )}
              </div>
              <span className="text-xs text-muted-foreground shrink-0">{Math.round((r.confidence || 0) * 100)}%</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── Publication Picker (no AI suggestion) ───

function PublicationPicker({ publications, selectedIds, onChange, multiMode, onToggleMultiMode }) {
  const [query, setQuery] = useState('');
  const [open, setOpen]   = useState(false);
  const wrapperRef        = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered      = publications
    .filter(p => p.name.toLowerCase().includes(query.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));
  const selectedPubs  = publications.filter(p => selectedIds.includes(p.id));

  const handleToggle = (pub) => {
    if (multiMode) {
      onChange(selectedIds.includes(pub.id) ? selectedIds.filter(id => id !== pub.id) : [...selectedIds, pub.id]);
    } else {
      onChange(selectedIds[0] === pub.id ? [] : [pub.id]);
      setOpen(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm text-[#6E6258] font-medium">
          Publication{multiMode ? 's' : ''}
        </label>
        <button onClick={onToggleMultiMode} className="text-xs text-blue-600 hover:underline">
          {multiMode ? 'Switch to single' : 'Post to multiple sites'}
        </button>
      </div>

      {multiMode && (
        <p className="text-xs text-muted-foreground bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
        </p>
      )}

      {selectedPubs.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedPubs.map(p => (
            <span key={p.id} className="flex items-center gap-1 bg-[#1F1A17] text-[#FFE4C4] text-xs px-2.5 py-1 rounded-full">
              {p.name}
              <button onClick={() => onChange(selectedIds.filter(id => id !== p.id))} className="hover:text-white ml-0.5">
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div ref={wrapperRef} className="relative">
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={query}
            onChange={e => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder="Search publications..."
            className="w-full pl-8 pr-3 py-2 border border-border rounded-lg text-sm bg-white text-[#1F1A17] placeholder:text-[#9C8E84]"
          />
        </div>
        {open && (
          <div className="absolute z-50 mt-1 w-full bg-white border border-border rounded-lg shadow-lg max-h-56 overflow-y-auto">
            {filtered.length === 0
              ? <div className="px-3 py-2 text-sm text-muted-foreground">No publications found</div>
              : filtered.map(p => (
                <button
                  key={p.id}
                  onClick={() => handleToggle(p)}
                  className="w-full text-left px-3 py-2 hover:bg-[#FFF4E8] text-sm flex items-center justify-between gap-2"
                >
                  <div>
                    <span className="text-foreground">{p.name}</span>
                    {p.domain && <span className="text-xs text-muted-foreground ml-2">{p.domain}</span>}
                    {p.site_type && (
                      <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${
                        p.site_type === 'industry'
                          ? 'bg-purple-100 text-purple-600'
                          : 'bg-blue-100 text-blue-600'
                      }`}>
                        {p.site_type === 'industry' ? 'Industry' : 'Geo'}
                      </span>
                    )}
                  </div>
                  {selectedIds.includes(p.id) && <Check size={14} className="text-emerald-600 shrink-0" />}
                </button>
              ))
            }
          </div>
        )}
      </div>
    </div>
  );
}


// ─── Tag Picker — all pub tags shown as chips, selected = dark, unselected = outlined ───

function TagPicker({ allTags, selectedTags, onChange, publicationIds }) {
  const availableTags = (publicationIds?.length > 0
    ? allTags.filter(t => publicationIds.some(id => String(id) === String(t.publication_id)))
    : []
  ).sort((a, b) => a.name.localeCompare(b.name));

  const handleToggle = (tagName) => {
    onChange(selectedTags.includes(tagName)
      ? selectedTags.filter(t => t !== tagName)
      : [...selectedTags, tagName]
    );
  };

  return (
    <div className="space-y-2">
      <label className="text-sm text-[#6E6258] font-medium block">
        Tags
        {!publicationIds?.length && (
          <span className="text-[#9C8E84] text-xs font-normal ml-1">— select a publication first</span>
        )}
      </label>

      {publicationIds?.length > 0 && availableTags.length === 0 && (
        <p className="text-xs text-muted-foreground italic">No tags set up for this publication.</p>
      )}

      {availableTags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {availableTags.map(t => {
            const selected = selectedTags.includes(t.name);
            return (
              <button
                key={t.id}
                onClick={() => handleToggle(t.name)}
                className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                  selected
                    ? 'bg-[#1F1A17] text-[#FFE4C4] border-[#1F1A17]'
                    : 'bg-white text-[#6E6258] border-[#E7D2B8] hover:border-[#C9B49A] hover:bg-[#FFF4E8]'
                }`}
              >
                {t.name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Local Areas Row — flat chip row, geo only, auto-matched pre-selected ───

function LocalAreasRow({ publicationId, publications, selectedAreas, onToggleArea, storyText }) {
  const [areas, setAreas]               = useState([]);
  const [loadingAreas, setLoadingAreas] = useState(false);
  const areaCache                       = useRef({});
  // Prevents auto-detect from re-running after areas reload on the same publication
  const autoDetectDone                  = useRef(false);

  const selectedPub = publications.find(p => p.id === publicationId);
  const isGeo       = selectedPub?.site_type === 'geo';

  // Reset autoDetectDone whenever publication changes
  useEffect(() => {
    autoDetectDone.current = false;
  }, [publicationId]);

  useEffect(() => {
    if (!publicationId || !isGeo) { setAreas([]); return; }
    if (areaCache.current[publicationId]) {
      setAreas(areaCache.current[publicationId]);
      return;
    }
    setLoadingAreas(true);
    api.get('/locations', { params: { publication_id: publicationId } })
      .then(res => {
        const data = res.data || [];
        areaCache.current[publicationId] = data;
        setAreas(data);
      })
      .catch(() => setAreas([]))
      .finally(() => setLoadingAreas(false));
  }, [publicationId, isGeo]);

  // Auto-select areas detected in story text — runs once per publication/areas load
  useEffect(() => {
    if (!areas.length || autoDetectDone.current) return;
    autoDetectDone.current = true;
    const areaNames     = areas.map(a => a.area_name);
    const detectedNames = matchTermsInText(areaNames, storyText || '');
    const detected      = areas
      .filter(a => detectedNames.includes(a.area_name))
      .map(a => ({ id: a.id, area_name: a.area_name }));
    if (detected.length > 0) {
      onToggleArea(prev => {
        const existingIds = (prev || []).map(a => a.id);
        const toAdd       = detected.filter(a => !existingIds.includes(a.id));
        return toAdd.length > 0 ? [...(prev || []), ...toAdd] : (prev || []);
      });
    }
  }, [areas]);

  if (!isGeo || !publicationId) return null;

  const isSelected = (areaId) => selectedAreas.some(a => a.id === areaId);

  const handleToggle = (area) => {
    if (isSelected(area.id)) {
      onToggleArea(selectedAreas.filter(a => a.id !== area.id));
    } else {
      onToggleArea([...selectedAreas, { id: area.id, area_name: area.area_name }]);
    }
  };

  return (
    <div className="space-y-2">
      <label className="text-sm text-[#6E6258] font-medium block">Local Areas</label>

      {loadingAreas && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 size={12} className="animate-spin" /> Loading areas...
        </div>
      )}

      {!loadingAreas && areas.length === 0 && (
        <p className="text-xs text-muted-foreground italic">No local areas set up for this publication.</p>
      )}

      {!loadingAreas && areas.length > 0 && (
        <div className="space-y-1.5">
          <div className="flex flex-wrap gap-1.5">
            {areas.map(area => {
              const selected = isSelected(area.id);
              return (
                <button
                  key={area.id}
                  onClick={() => handleToggle(area)}
                  className={`flex items-center gap-1 text-xs px-3 py-1 rounded-full border transition-colors ${
                    selected
                      ? 'bg-[#1F1A17] text-[#FFE4C4] border-[#1F1A17]'
                      : 'bg-white text-[#6E6258] border-[#E7D2B8] hover:border-[#C9B49A] hover:bg-[#FFF4E8]'
                  }`}
                >
                  <MapPin size={9} />
                  {area.area_name}
                </button>
              );
            })}
          </div>
          {selectedAreas.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {selectedAreas.length} area{selectedAreas.length !== 1 ? 's' : ''} selected
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───

export default function NewStoryPage() {
  const { user } = useAuth();
  const navigate  = useNavigate();
  const [step, setStep]               = useState(1);
  const [submitting, setSubmitting]   = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [loadError, setLoadError]     = useState(false);
  const [clients, setClients]         = useState([]);
  const [publications, setPublications] = useState([]);
  const [allTags, setAllTags]         = useState([]);
  const [bylines, setBylines]         = useState([]);
  const [storyTypes, setStoryTypes]   = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [persons, setPersons]         = useState([]);

  const [publicationMultiMode, setPublicationMultiMode] = useState(false);
  const [generatingDraft, setGeneratingDraft] = useState(false);
  const [draftGenerated, setDraftGenerated]   = useState(false);
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);

  const [aiImageCaption, setAiImageCaption] = useState('');
  const [aiImageAltText, setAiImageAltText] = useState('');
  const [aiImageTags, setAiImageTags]       = useState([]);
  const [aiPromptSuggestion, setAiPromptSuggestion] = useState('');

  const [autoOrgDialog, setAutoOrgDialog]       = useState(false);
  const [autoPersonDialog, setAutoPersonDialog] = useState(false);
  const [autoPrefillName, setAutoPrefillName]   = useState('');
  const [orgMatchDialog, setOrgMatchDialog]       = useState(false);
  const [personMatchDialog, setPersonMatchDialog] = useState(false);

  const [bylineDialog, setBylineDialog]             = useState(false);
  const [newBylineName, setNewBylineName]           = useState('');
  const [manageBylineDialog, setManageBylineDialog] = useState(false);
  const [manageStoryTypeDialog, setManageStoryTypeDialog] = useState(false);
  const [newStoryTypeLabel, setNewStoryTypeLabel]   = useState('');

  const [areasByPub, setAreasByPub] = useState({});

  const isAdmin = user?.role === 'admin';

  const [form, setForm] = useState({
    source_link: '', source_name: '', angle: '',
    headline: '', lede: '', body: '',
    estimated_read_time: '', image: null, image_caption: '', image_accreditation: '',
    media_asset_id: null, client_name: '', project_name: '',
    publication_ids: [], byline_id: '', tags: [], tagsByPub: {},
    story_type: 'press_release', story_subject_type: 'organisation',
    commercial_label_id: null,
    person_id: '', person_name: '', organisation_id: '', organisation_name: '',
  });

  const fetchData = useCallback(async () => {
    setLoadingData(true);
    setLoadError(false);
    const [c, p, t, b, o, pr, st] = await Promise.all([
      fetchWithRetry(() => api.get('/clients')),
      fetchWithRetry(() => api.get('/publications')),
      fetchWithRetry(() => api.get('/tags')),
      fetchWithRetry(() => api.get('/bylines')),
      fetchWithRetry(() => api.get('/organisations')),
      fetchWithRetry(() => api.get('/persons')),
      fetchWithRetry(() => api.get('/story-types')),
    ]);
    if (c)  setClients(c.data);
    if (p)  setPublications(p.data);
    if (t)  setAllTags(t.data);
    if (b)  setBylines(b.data);
    if (o)  setOrganisations(o.data);
    if (pr) setPersons(pr.data);
    if (st && st.data.length > 0) setStoryTypes(st.data);
    else    setStoryTypes(defaultStoryTypes);
    if (!c || !p) setLoadError(true);
    setLoadingData(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const update = useCallback((key, val) => setForm(prev => ({ ...prev, [key]: val })), []);

  const setAreasFor = useCallback((pubId, next) => {
    if (!pubId) return;
    setAreasByPub(prev => {
      const current = prev[pubId] || [];
      const value   = typeof next === 'function' ? next(current) : next;
      return { ...prev, [pubId]: value };
    });
  }, []);

  const score          = calcScore(form);
  const selectedClient = clients.find(c => c.name === form.client_name);
  const hasExistingContent = !!(form.headline || form.lede || (form.body && form.body !== '<p></p>'));

  // Build story text for rule-based matching (headline + body stripped of HTML)
  const storyText = `${form.headline || ''} ${(form.body || '').replace(/<[^>]+>/g, ' ')} ${form.lede || ''}`.trim();

  const handleEnterStep2 = () => {
    setStep(2);
    if (!form.publication_ids.length) return;
    // Auto-select tags per publication based on story content
    const newTagsByPub = { ...form.tagsByPub };
    form.publication_ids.forEach(pubId => {
      const pubTags  = allTags.filter(t => String(t.publication_id) === String(pubId)).map(t => t.name);
      const matched  = matchTermsInText(pubTags, storyText);
      const existing = newTagsByPub[pubId] || [];
      newTagsByPub[pubId] = [...new Set([...existing, ...matched])];
    });
    update('tagsByPub', newTagsByPub);
    if (!publicationMultiMode && form.publication_ids.length === 1) {
      update('tags', newTagsByPub[form.publication_ids[0]] || []);
    }
  };

  const tryAutoMatchOrg = useCallback(async (suggestedName) => {
    if (!suggestedName) return;
    try {
      const res = await api.get('/organisations/match', { params: { q: suggestedName } });
      const matches = res.data || [];
      if (matches.length > 0 && matches[0].confidence >= 0.75) {
        setForm(prev => ({ ...prev, organisation_id: matches[0].id, organisation_name: matches[0].name }));
        setOrganisations(prev => prev.find(o => o.id === matches[0].id) ? prev : [...prev, matches[0]]);
        toast.success(`Organisation matched: ${matches[0].name}`);
      } else {
        setAutoPrefillName(suggestedName);
        setAutoOrgDialog(true);
      }
    } catch {
      setAutoPrefillName(suggestedName);
      setAutoOrgDialog(true);
    }
  }, []);

  const tryAutoMatchPerson = useCallback(async (suggestedName) => {
    if (!suggestedName) return;
    try {
      const res = await api.get('/persons/match', { params: { q: suggestedName } });
      const matches = res.data || [];
      if (matches.length > 0 && matches[0].confidence >= 0.75) {
        setForm(prev => ({ ...prev, person_id: matches[0].id, person_name: matches[0].name }));
        setPersons(prev => prev.find(p => p.id === matches[0].id) ? prev : [...prev, matches[0]]);
        toast.success(`Person matched: ${matches[0].name}`);
      } else {
        setAutoPrefillName(suggestedName);
        setAutoPersonDialog(true);
      }
    } catch {
      setAutoPrefillName(suggestedName);
      setAutoPersonDialog(true);
    }
  }, []);

  const applyDraft = useCallback(async () => {
    setGeneratingDraft(true);
    try {
      const res = await api.post('/stories/generate-draft', {
        source_link:        form.source_link,
        story_subject_type: form.story_subject_type,
        angle:              form.angle,
      });
      const d = res.data;

      setForm(prev => ({
        ...prev,
        headline:            d.headline            || prev.headline,
        lede:                d.lede                || prev.lede,
        body:                d.body                || prev.body,
        estimated_read_time: d.estimated_read_time ?? prev.estimated_read_time,
        source_name:         d.source_name         || prev.source_name,
        story_type:          d.story_type          || prev.story_type,
        geography:           d.geography           || prev.geography,
      }));

      if (d.image_prompt)       setAiPromptSuggestion(d.image_prompt);
      if (d.image_caption)      setAiImageCaption(d.image_caption);
      if (d.image_alt_text)     setAiImageAltText(d.image_alt_text);
      if (d.image_tags?.length) setAiImageTags(d.image_tags);

      if (form.story_subject_type === 'organisation' && d.suggested_organisation) {
        await tryAutoMatchOrg(d.suggested_organisation);
      } else if (form.story_subject_type === 'person' && d.suggested_person) {
        await tryAutoMatchPerson(d.suggested_person);
      }

      toast.success('Draft populated — review before submitting');
      setDraftGenerated(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Draft generation failed. Check your source URL and try again.');
    } finally {
      setGeneratingDraft(false);
    }
  }, [form.source_link, form.story_subject_type, form.angle, tryAutoMatchOrg, tryAutoMatchPerson]);

  const handleGenerateDraft = () => {
    if (!form.source_link) return;
    if (hasExistingContent && !draftGenerated) { setConfirmOverwrite(true); } else { applyDraft(); }
  };

  const handleMediaSelected = ({ file_url, media_asset_id, caption, accreditation }) => {
    update('image', file_url);
    update('media_asset_id', media_asset_id);
    if (caption && !form.image_caption) update('image_caption', caption);
    if (accreditation && !form.image_accreditation) update('image_accreditation', accreditation);
  };

  const handleOrgSelected = ({ id, name, aliases }) => {
    setForm(prev => ({ ...prev, organisation_id: id, organisation_name: name, person_id: '', person_name: '' }));
    setOrganisations(prev => prev.find(o => o.id === id) ? prev : [...prev, { id, name, aliases }]);
  };

  const handlePersonSelected = ({ id, name, title }) => {
    setForm(prev => ({ ...prev, person_id: id, person_name: name, organisation_id: '', organisation_name: '' }));
    setPersons(prev => prev.find(p => p.id === id) ? prev : [...prev, { id, name, title }]);
  };

  const handleAddByline = async () => {
    if (!newBylineName.trim()) return;
    try {
      const res = await api.post('/bylines', { name: newBylineName.trim() });
      setBylines(prev => [...prev, res.data]);
      update('byline_id', res.data.id);
      setBylineDialog(false); setNewBylineName('');
      toast.success('Byline created');
    } catch { toast.error('Failed to create byline'); }
  };

  const handleDeleteByline = async (bylineId) => {
    try {
      await api.delete(`/bylines/${bylineId}`);
      setBylines(prev => prev.filter(b => b.id !== bylineId));
      if (form.byline_id === bylineId) update('byline_id', '');
      toast.success('Byline removed');
    } catch { toast.error('Failed to delete byline'); }
  };

  const handleAddStoryType = async () => {
    if (!newStoryTypeLabel.trim()) return;
    try {
      const res = await api.post('/story-types', { label: newStoryTypeLabel.trim() });
      setStoryTypes(prev => [...prev, res.data]); setNewStoryTypeLabel('');
      toast.success('Story type created');
    } catch { toast.error('Failed to create story type'); }
  };

  const handleDeleteStoryType = async (typeId) => {
    try {
      await api.delete(`/story-types/${typeId}`);
      setStoryTypes(prev => prev.filter(t => t.id !== typeId));
      toast.success('Story type removed');
    } catch { toast.error('Failed to delete story type'); }
  };

  const availableStoryTypes = storyTypes.length > 0 ? storyTypes : defaultStoryTypes;

  const handleSubmit = async () => {
    if (!form.headline)    { toast.error('Headline is required'); return; }
    if (!form.client_name) { toast.error('Client is required'); return; }
    setSubmitting(true);
    try {
      const pubIds = form.publication_ids;

      const tagsByPublication = publicationMultiMode
        ? pubIds.reduce((acc, id) => ({ ...acc, [id]: form.tagsByPub[id] || [] }), {})
        : (pubIds[0] ? { [pubIds[0]]: form.tags } : {});

      const unionTags = publicationMultiMode
        ? [...new Set(Object.values(form.tagsByPub).flat())]
        : form.tags;

      const payload = {
        ...form,
        tags:                 unionTags,
        publication_ids:      pubIds,
        tags_by_publication:  tagsByPublication,
        estimated_read_time:  form.estimated_read_time ? parseInt(form.estimated_read_time) : null,
      };
      delete payload.tagsByPub;
      if (!payload.byline_id) delete payload.byline_id;

      const res     = await api.post('/stories', payload);
      const created = res.data?.group_stories?.length
        ? res.data.group_stories
        : [{ id: res.data?.id, publication_id: pubIds[0] || null }];

      const locationCalls = created.flatMap(st => {
        const areas = areasByPub[st.publication_id] || [];
        return areas.map(loc =>
          api.post(`/stories/${st.id}/locations`, { location_id: loc.id, auto_tagged: false })
        );
      });
      if (locationCalls.length > 0) await Promise.allSettled(locationCalls);

      if (form.media_asset_id) {
        await Promise.allSettled(created.map(st =>
          api.post(`/media/assets/${form.media_asset_id}/usage`, {
            used_by_app:    'tempust',
            story_id:       st.id,
            publication_id: st.publication_id,
          })
        ));
      }

      toast.success(created.length > 1
        ? `Submitted to ${created.length} publications`
        : 'Story submitted for review!');
      navigate('/stories');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit story');
    } finally { setSubmitting(false); }
  };

  const label   = 'text-sm text-[#6E6258] font-medium mb-1.5 block';
  const cardCls = 'bg-card border-border';

  if (loadingData) {
    return (
      <div className="space-y-6 max-w-4xl">
        <div className="h-9 w-48 bg-[#F8EBD9] rounded animate-pulse" />
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 size={16} className="animate-spin" /> Loading form data...
        </div>
        {[1, 2, 3].map(i => (
          <div key={i} className="h-32 bg-[#FFF4E8] border border-border rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="new-story-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-foreground font-outfit tracking-tight">New Story</h1>
        <div className="flex items-center gap-2 text-sm">
          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${step >= 1 ? 'bg-[#1F1A17] text-[#FFE4C4]' : 'bg-[#F8EBD9] text-[#9C8E84]'}`}>1</span>
          <span className="text-muted-foreground text-xs hidden sm:block">Content</span>
          <div className="w-8 h-px bg-border" />
          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${step >= 2 ? 'bg-[#1F1A17] text-[#FFE4C4]' : 'bg-[#F8EBD9] text-[#9C8E84]'}`}>2</span>
          <span className="text-muted-foreground text-xs hidden sm:block">Distribution</span>
        </div>
      </div>

      {loadError && (
        <div className="flex items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          <div className="flex items-center gap-2">
            <AlertCircle size={16} className="text-amber-600 shrink-0" />
            <span className="text-sm text-amber-700">Some data failed to load. Click Retry.</span>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchData} className="text-amber-600 hover:text-amber-700 hover:bg-amber-100 shrink-0 gap-1.5">
            <RefreshCw size={14} /> Retry
          </Button>
        </div>
      )}

      <Card className={cardCls}>
        <CardContent className="py-3 px-5 flex items-center gap-4">
          {score >= 80 ? <CheckCircle size={18} className="text-emerald-600" /> : <AlertCircle size={18} className="text-amber-600" />}
          <span className="text-sm text-muted-foreground">Completeness</span>
          <Progress value={score} className="flex-1 h-2 bg-[#F8EBD9]" />
          <span className={`text-sm font-bold ${score >= 80 ? 'text-emerald-600' : 'text-amber-600'}`}>{score}%</span>
        </CardContent>
      </Card>

      {step === 1 && (
        <div className="space-y-5 mt-6">

          <Card className={cardCls}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-foreground font-outfit">Story Subject</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">Set this before generating a draft — it shapes what the AI focuses on.</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Button
                  variant={form.story_subject_type === 'organisation' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { update('story_subject_type', 'organisation'); update('person_id', ''); update('person_name', ''); }}
                  className={form.story_subject_type === 'organisation' ? 'bg-[#1F1A17] text-[#FFE4C4] hover:bg-[#2B241F]' : 'bg-transparent border-border text-muted-foreground hover:bg-[#FFF4E8] hover:text-foreground'}
                  data-testid="subject-type-org"
                >Organisation</Button>
                <Button
                  variant={form.story_subject_type === 'person' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { update('story_subject_type', 'person'); update('organisation_id', ''); update('organisation_name', ''); }}
                  className={form.story_subject_type === 'person' ? 'bg-[#1F1A17] text-[#FFE4C4] hover:bg-[#2B241F]' : 'bg-transparent border-border text-muted-foreground hover:bg-[#FFF4E8] hover:text-foreground'}
                  data-testid="subject-type-person"
                >Person</Button>
              </div>

              {form.story_subject_type === 'organisation' && (
                <div>
                  <label className={label}>Organisation</label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <EntitySearchCombobox
                        entityType="organisation"
                        value={form.organisation_id ? { id: form.organisation_id, name: form.organisation_name } : null}
                        onSelect={handleOrgSelected}
                        onClear={() => { update('organisation_id', ''); update('organisation_name', ''); }}
                        placeholder="Search organisations..."
                      />
                    </div>
                    <Button variant="outline" size="icon" onClick={() => setOrgMatchDialog(true)}
                      className="bg-transparent border-border text-muted-foreground hover:bg-[#FFF4E8] hover:text-foreground shrink-0"
                      data-testid="add-org-btn">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}

              {form.story_subject_type === 'person' && (
                <div>
                  <label className={label}>Person</label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <EntitySearchCombobox
                        entityType="person"
                        value={form.person_id ? { id: form.person_id, name: form.person_name } : null}
                        onSelect={handlePersonSelected}
                        onClear={() => { update('person_id', ''); update('person_name', ''); }}
                        placeholder="Search persons..."
                      />
                    </div>
                    <Button variant="outline" size="icon" onClick={() => setPersonMatchDialog(true)}
                      className="bg-transparent border-border text-muted-foreground hover:bg-[#FFF4E8] hover:text-foreground shrink-0"
                      data-testid="add-person-btn">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className={cardCls}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-foreground font-outfit">Source Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={label}>Source Link</label>
                  <Input
                    data-testid="source-link-input"
                    value={form.source_link}
                    onChange={e => { update('source_link', e.target.value); setDraftGenerated(false); }}
                    placeholder="https://..."
                    className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]"
                  />
                </div>
                <div>
                  <label className={label}>Source Name</label>
                  <Input
                    data-testid="source-name-input"
                    value={form.source_name}
                    onChange={e => update('source_name', e.target.value)}
                    placeholder="e.g., Reuters"
                    className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]"
                  />
                </div>
              </div>

              {form.source_link && (
                <div className="space-y-3">
                  <div>
                    <label className={label}>
                      Angle
                      <span className="text-[#9C8E84] font-normal ml-1">— optional narrative direction for the AI</span>
                    </label>
                    <Textarea
                      value={form.angle}
                      onChange={e => update('angle', e.target.value)}
                      placeholder=" "
                      className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84] min-h-[72px] resize-none"
                    />
                  </div>
                  <div className="flex items-center justify-between bg-[#FFF4E8] border border-[#E7D2B8] rounded-lg px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <Sparkles size={16} className="text-amber-500 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-foreground">AI Draft</p>
                        <p className="text-xs text-[#9C8E84]">
                          Focused on <span className="font-medium">{form.story_subject_type === 'organisation' ? 'organisation' : 'person'}</span> — change subject above to switch intent.
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={handleGenerateDraft}
                      disabled={generatingDraft}
                      size="sm"
                      className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4] gap-2 shrink-0 ml-4"
                      data-testid="generate-draft-btn"
                    >
                      {generatingDraft
                        ? <><Loader2 size={14} className="animate-spin" />Generating...</>
                        : <><Sparkles size={14} />Get Suggested Draft</>
                      }
                    </Button>
                  </div>
                </div>
              )}

              {generatingDraft && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 size={14} className="animate-spin text-amber-500" />
                  Fetching article and writing story — this takes a few seconds...
                </div>
              )}

              {draftGenerated && !generatingDraft && (
                <p className="text-xs text-emerald-600 flex items-center gap-1.5">
                  <CheckCircle size={13} />
                  Draft populated — organisation and image metadata pre-filled where possible.
                </p>
              )}
            </CardContent>
          </Card>

          <Card className={cardCls}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-foreground font-outfit">Featured Image</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                <div className="flex-1">
                  {form.image ? (
                    <div className="relative">
                      <img src={form.image} alt="Featured" className="w-full rounded-lg aspect-video object-cover" data-testid="story-image-preview" />
                      <div className="absolute top-2 right-2 flex gap-1.5">
                        <Button variant="ghost" size="icon" className="bg-black/40 hover:bg-black/60 text-white h-8 w-8" onClick={() => setMediaPickerOpen(true)}>
                          <ImageIcon size={14} />
                        </Button>
                        <Button variant="ghost" size="icon" className="bg-black/40 hover:bg-black/60 text-white h-8 w-8" onClick={() => { update('image', null); update('media_asset_id', null); }}>
                          <X size={16} />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setMediaPickerOpen(true)}
                      className="flex flex-col items-center justify-center py-8 border-2 border-dashed border-[#E7D2B8] rounded-lg cursor-pointer hover:border-[#C9B49A] hover:bg-[#FFF4E8] transition-colors w-full min-h-[140px]"
                      data-testid="image-upload-area"
                    >
                      <ImageIcon size={28} className="text-[#C9B49A] mb-2" />
                      <span className="text-sm text-muted-foreground">Click to select from media library</span>
                      {aiImageCaption && <span className="text-xs text-amber-600 mt-1">AI caption: {aiImageCaption}</span>}
                    </button>
                  )}
                </div>
                <div className="w-64 flex flex-col gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Caption</label>
                    <Input
                      data-testid="image-caption-input"
                      value={form.image_caption}
                      onChange={e => update('image_caption', e.target.value)}
                      placeholder={aiImageCaption || 'Image caption'}
                      className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Accreditation</label>
                    <Input
                      data-testid="image-accreditation-input"
                      value={form.image_accreditation}
                      onChange={e => update('image_accreditation', e.target.value)}
                      placeholder="Accreditation"
                      className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]"
                    />
                  </div>
                  {aiImageTags.length > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">AI image tags</p>
                      <div className="flex flex-wrap gap-1">
                        {aiImageTags.map(tag => (
                          <span key={tag} className="text-xs bg-amber-50 border border-amber-200 text-amber-700 px-1.5 py-0.5 rounded">{tag}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={cardCls}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-foreground font-outfit">Story Content</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className={label}>Headline *</label>
                <Input data-testid="headline-input" value={form.headline} onChange={e => update('headline', e.target.value)}
                  placeholder="Enter story headline" className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84] text-lg font-semibold" />
              </div>
              <div>
                <label className={label}>Intro</label>
                <Textarea data-testid="lede-input" value={form.lede} onChange={e => update('lede', e.target.value)}
                  placeholder="Brief summary..." className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84] min-h-[80px]" />
              </div>
              <div>
                <label className={label}>Body</label>
                <TipTapEditor
                  key={draftGenerated ? 'draft' : 'manual'}
                  content={form.body}
                  onChange={val => update('body', val)}
                  placeholder="Paste or write story content..."
                  mediaContext={{
                    organisationId:   form.organisation_id,
                    organisationName: form.organisation_name,
                    personId:         form.person_id,
                    personName:       form.person_name,
                    tags:             form.tags,
                    publicationId:    form.publication_ids[0] || null,
                    organisations,
                  }}
                />
              </div>
              <div className="w-32">
                <label className={label}>Read Time (min)</label>
                <Input data-testid="read-time-input" type="number" min="1" value={form.estimated_read_time}
                  onChange={e => update('estimated_read_time', e.target.value)} placeholder="3"
                  className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]" />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button
              data-testid="next-step-btn"
              onClick={handleEnterStep2}
              className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]"
            >
              Next: Distribution <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-5 mt-6">

          <Card className={cardCls}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-foreground font-outfit">Client & Project</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={label}>Client *</label>
                <Select value={form.client_name} onValueChange={v => { update('client_name', v); update('project_name', ''); }}>
                  <SelectTrigger className="bg-white text-[#1F1A17] border-border" data-testid="client-select-trigger">
                    <SelectValue placeholder={clients.length === 0 ? 'No clients yet' : 'Select client'} />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-border">
                    {clients.map(c => <SelectItem key={c.id} value={c.name} className="text-[#1F1A17]">{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className={label}>Project</label>
                <Select value={form.project_name} onValueChange={v => update('project_name', v)} disabled={!selectedClient}>
                  <SelectTrigger className="bg-white text-[#1F1A17] border-border" data-testid="project-select-trigger">
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-border">
                    {selectedClient?.projects?.map(p => <SelectItem key={p} value={p} className="text-[#1F1A17]">{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card className={cardCls}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base text-foreground font-outfit">Classification</CardTitle>
                {isAdmin && (
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setManageStoryTypeDialog(true)}
                      className="text-muted-foreground hover:text-foreground hover:bg-[#FFF4E8] h-7 px-2">
                      <Settings size={14} className="mr-1" />Types
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setManageBylineDialog(true)}
                      className="text-muted-foreground hover:text-foreground hover:bg-[#FFF4E8] h-7 px-2">
                      <Settings size={14} className="mr-1" />Bylines
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={label}>Story Type</label>
                <Select value={form.story_type} onValueChange={v => update('story_type', v)}>
                  <SelectTrigger className="bg-white text-[#1F1A17] border-border"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-border">
                    {availableStoryTypes.map(t => <SelectItem key={t.value} value={t.value} className="text-[#1F1A17]">{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className={label}>Byline</label>
                <div className="flex gap-2">
                  <Select value={form.byline_id} onValueChange={v => update('byline_id', v)}>
                    <SelectTrigger className="bg-white text-[#1F1A17] border-border flex-1">
                      <SelectValue placeholder={bylines.length === 0 ? 'No bylines yet' : 'Select byline'} />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-border">
                      {bylines.map(b => <SelectItem key={b.id} value={b.id} className="text-[#1F1A17]">{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="icon" onClick={() => setBylineDialog(true)}
                    className="bg-transparent border-border text-muted-foreground hover:bg-[#FFF4E8] hover:text-foreground shrink-0">
                    <Plus size={16} />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={cardCls}>
            <CardContent className="pt-5">
              <CommercialLabelSelect
                value={form.commercial_label_id}
                onChange={v => update('commercial_label_id', v)}
                canManage={isAdmin}
              />
            </CardContent>
          </Card>

          <Card className={cardCls}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-foreground font-outfit">Target Publication & Tags</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <PublicationPicker
                publications={publications}
                selectedIds={form.publication_ids}
                onChange={ids => {
                  update('publication_ids', ids);
                  const cleanedTags = Object.fromEntries(
                    Object.entries(form.tagsByPub).filter(([id]) => ids.includes(id))
                  );
                  update('tagsByPub', cleanedTags);
                  setAreasByPub(prev => Object.fromEntries(
                    Object.entries(prev).filter(([id]) => ids.includes(id))
                  ));
                }}
                multiMode={publicationMultiMode}
                onToggleMultiMode={() => setPublicationMultiMode(v => !v)}
              />

              {!publicationMultiMode && (
                <div className="space-y-4">
                  <TagPicker
                    allTags={allTags}
                    selectedTags={form.tags}
                    onChange={tags => update('tags', tags)}
                    publicationIds={form.publication_ids}
                  />
                  <LocalAreasRow
                    publicationId={form.publication_ids[0] || null}
                    publications={publications}
                    selectedAreas={areasByPub[form.publication_ids[0]] || []}
                    onToggleArea={next => setAreasFor(form.publication_ids[0], next)}
                    storyText={storyText}
                  />
                </div>
              )}

              {publicationMultiMode && form.publication_ids.length > 0 && (
                <div className="space-y-4">
                  {form.publication_ids.map(pubId => {
                    const pub = publications.find(p => p.id === pubId);
                    if (!pub) return null;
                    return (
                      <div key={pubId} className="rounded-lg border border-border p-4 space-y-3">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                            pub.site_type === 'industry'
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {pub.site_type === 'industry' ? 'Industry' : 'Geo'}
                          </span>
                          <span className="text-sm font-medium text-foreground">{pub.name}</span>
                          {pub.domain && (
                            <span className="text-xs text-muted-foreground">{pub.domain}</span>
                          )}
                        </div>
                        <TagPicker
                          allTags={allTags}
                          selectedTags={form.tagsByPub[pubId] || []}
                          onChange={tags => update('tagsByPub', { ...form.tagsByPub, [pubId]: tags })}
                          publicationIds={[pubId]}
                        />
                        <LocalAreasRow
                          publicationId={pubId}
                          publications={publications}
                          selectedAreas={areasByPub[pubId] || []}
                          onToggleArea={next => setAreasFor(pubId, next)}
                          storyText={storyText}
                        />
                      </div>
                    );
                  })}
                  <p className="text-xs text-muted-foreground">
                    Each publication gets its own story with the tags and areas chosen above.
                  </p>
                </div>
              )}

              {publicationMultiMode && form.publication_ids.length === 0 && (
                <p className="text-xs text-muted-foreground italic">Select publications above to see their tags.</p>
              )}
            </CardContent>
          </Card>

          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => setStep(1)} className="text-muted-foreground hover:text-foreground hover:bg-[#FFF4E8]" data-testid="back-step-btn">
              <ArrowLeft size={16} className="mr-2" /> Back
            </Button>
            <Button onClick={handleSubmit} disabled={submitting} className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]" data-testid="submit-story-btn">
              {submitting
                ? <><Loader2 size={16} className="mr-2 animate-spin" />Submitting...</>
                : <><Send size={16} className="mr-2" />
                    {publicationMultiMode && form.publication_ids.length > 1
                      ? `Submit to ${form.publication_ids.length} Sites`
                      : 'Submit to Review'}
                  </>
              }
            </Button>
          </div>
        </div>
      )}

      <MediaPickerModal
        open={mediaPickerOpen}
        onOpenChange={setMediaPickerOpen}
        organisationId={form.organisation_id}
        organisationName={form.organisation_name}
        personId={form.person_id}
        personName={form.person_name}
        tags={publicationMultiMode ? [...new Set(Object.values(form.tagsByPub).flat())] : form.tags}
        publicationId={form.publication_ids[0] || null}
        currentAssetId={form.media_asset_id}
        aiImageCaption={aiImageCaption}
        aiImageAltText={aiImageAltText}
        aiImageTags={aiImageTags}
        aiImageAccreditation={form.image_accreditation}
        aiPromptSuggestion={aiPromptSuggestion}
        organisations={organisations}
        onSelect={handleMediaSelected}
      />

      <Dialog open={confirmOverwrite} onOpenChange={setConfirmOverwrite}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-foreground">Overwrite existing content?</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Generating a draft will replace the headline, intro, body and other AI-fillable fields. Image, client and publication selections are kept.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOverwrite(false)} className="text-muted-foreground hover:text-foreground">Cancel</Button>
            <Button onClick={() => { setConfirmOverwrite(false); applyDraft(); }} className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]">Yes, overwrite</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bylineDialog} onOpenChange={setBylineDialog}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-foreground">Add Byline</DialogTitle>
            <DialogDescription className="text-muted-foreground">Create a new byline for attribution.</DialogDescription>
          </DialogHeader>
          <Input value={newBylineName} onChange={e => setNewBylineName(e.target.value)} placeholder="Byline name"
            className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]" data-testid="new-byline-input"
            onKeyDown={e => e.key === 'Enter' && handleAddByline()} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBylineDialog(false)} className="text-muted-foreground hover:text-foreground">Cancel</Button>
            <Button onClick={handleAddByline} className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]" data-testid="save-byline-btn">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={manageBylineDialog} onOpenChange={setManageBylineDialog}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground">Manage Bylines</DialogTitle>
            <DialogDescription className="text-muted-foreground">Add or remove bylines available for stories.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input value={newBylineName} onChange={e => setNewBylineName(e.target.value)} placeholder="New byline name"
                className="bg-white text-[#1F1A17] border-border flex-1"
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddByline())} />
              <Button onClick={handleAddByline} disabled={!newBylineName.trim()} className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]">
                <Plus size={16} />
              </Button>
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1">
              {bylines.map(b => (
                <div key={b.id} className="flex items-center justify-between p-2 bg-[#FFF4E8] rounded">
                  <span className="text-sm text-foreground">{b.name}</span>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteByline(b.id)}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50 h-7 w-7 p-0">
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
              {bylines.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No bylines yet</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setManageBylineDialog(false)} className="text-muted-foreground hover:text-foreground">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={manageStoryTypeDialog} onOpenChange={setManageStoryTypeDialog}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground">Manage Story Types</DialogTitle>
            <DialogDescription className="text-muted-foreground">Add or remove story type categories.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input value={newStoryTypeLabel} onChange={e => setNewStoryTypeLabel(e.target.value)}
                placeholder="New story type" className="bg-white text-[#1F1A17] border-border flex-1"
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddStoryType())} />
              <Button onClick={handleAddStoryType} disabled={!newStoryTypeLabel.trim()} className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]">
                <Plus size={16} />
              </Button>
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1">
              {storyTypes.map(t => (
                <div key={t.id} className="flex items-center justify-between p-2 bg-[#FFF4E8] rounded">
                  <span className="text-sm text-foreground">{t.label}</span>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteStoryType(t.id)}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50 h-7 w-7 p-0">
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
              {storyTypes.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No story types yet</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setManageStoryTypeDialog(false)} className="text-muted-foreground hover:text-foreground">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <OrgMatchDialog open={autoOrgDialog} onOpenChange={setAutoOrgDialog} onSelect={handleOrgSelected} setOrganisations={setOrganisations} prefillName={autoPrefillName} />
      <PersonMatchDialog open={autoPersonDialog} onOpenChange={setAutoPersonDialog} onSelect={handlePersonSelected} organisations={organisations} setPersons={setPersons} prefillName={autoPrefillName} />
      <OrgMatchDialog open={orgMatchDialog} onOpenChange={setOrgMatchDialog} onSelect={handleOrgSelected} setOrganisations={setOrganisations} />
      <PersonMatchDialog open={personMatchDialog} onOpenChange={setPersonMatchDialog} onSelect={handlePersonSelected} organisations={organisations} setPersons={setPersons} />
    </div>
  );
}